import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { FORM_FIELD_CLASS } from '@/components/ui/form-control-styles'
import {
  confirmBrandBtnClass,
  confirmDeleteCancelBtnClass,
  confirmDeleteDialogContentClass,
} from '@/components/shared/ConfirmDeleteDialog'
import { SettingsCard } from '@/components/settings/SettingsCard'
import { useRequestAccountDeletion } from '@/hooks/useCompliance'
import { useAppDispatch } from '@/store/hooks'
import { logout } from '@/store/authSlice'
import { toast } from '@/components/ui/toast'
import { cn } from '@/lib/utils'
import axios from 'axios'
import type { AccountDeletionReason } from '@/types'

const REASONS: AccountDeletionReason[] = [
  'No longer needed',
  'Privacy concerns',
  'Other',
]
// Map the API enum value -> translation key for its display label.
const REASON_LABEL_KEY: Record<AccountDeletionReason, string> = {
  'No longer needed': 'accountDeletion.reasons.noLongerNeeded',
  'Privacy concerns': 'accountDeletion.reasons.privacyConcerns',
  Other: 'accountDeletion.reasons.other',
}
const NONE = '__none__'

// 2.13.1 — Account deletion (GDPR Art. 17). Confirmation dialog with optional
// reason, mandatory password re-entry, exact error strings, and logout on
// success.
export function AccountDeletionPanel() {
  const { t } = useTranslation('compliance')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const request = useRequestAccountDeletion()

  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState<AccountDeletionReason | ''>('')
  const [otherReason, setOtherReason] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)

  function reset() {
    setReason('')
    setOtherReason('')
    setPassword('')
    setError(null)
  }

  function confirm() {
    if (!password.trim()) {
      setError(t('accountDeletion.errors.passwordRequired'))
      return
    }
    setError(null)
    const submittedReason =
      reason === 'Other' ? otherReason.trim() || reason : reason || undefined
    request.mutate(
      { reason: submittedReason, password },
      {
        onSuccess: async () => {
          toast({
            variant: 'success',
            title: t('accountDeletion.success'),
          })
          setOpen(false)
          await dispatch(logout(undefined))
          navigate('/login', { replace: true })
        },
        onError: e => {
          // Map server responses to the AC-specified error strings.
          if (axios.isAxiosError(e)) {
            if (!e.response) {
              setError(t('accountDeletion.errors.verifyFailed'))
              return
            }
            if (e.response.status === 401 || e.response.status === 422) {
              setError(t('accountDeletion.errors.incorrectPassword'))
              return
            }
          }
          setError(t('accountDeletion.errors.generic'))
        },
      }
    )
  }

  return (
    <SettingsCard
      title={t('accountDeletion.title')}
      description={t('accountDeletion.description')}
      className='border-[#e7e9ef] shadow-[0px_1px_4px_-4px_rgba(38,45,66,0.05),0px_8px_16px_-4px_rgba(38,45,66,0.1)]'
      headerClassName='items-start border-[#e7e9ef] bg-[#fbfbfc] px-4 pb-2 pt-4 [&>div:last-child]:hidden'
      titleClassName='text-sm leading-[1.2] text-[#171a20]'
      descriptionClassName='mt-1.5 text-xs font-normal leading-4 text-[#6c7285]'
      contentClassName='flex h-[70px] items-center border-b border-[#e7e9ef] px-4 py-3'
    >
      <Button
        variant='destructive'
        className='gap-2 rounded-xl bg-[#ff5a1f] px-3 text-sm font-medium leading-none text-white hover:bg-[#ff5a1f]/90'
        onClick={() => setOpen(true)}
      >
        <Trash2 className='size-4' /> {t('accountDeletion.button')}
      </Button>

      <Dialog
        open={open}
        onOpenChange={o => {
          setOpen(o)
          if (!o) reset()
        }}
      >
        <DialogContent
          className={confirmDeleteDialogContentClass}
          overlayClassName='bg-black/70 backdrop-blur-[2px]'
          showCloseButton={false}
        >
          <div className='flex flex-col gap-6'>
            <div className='flex flex-col gap-2'>
              <DialogTitle className='font-heading text-base font-semibold leading-[1.2] text-[#171a20]'>
                {t('accountDeletion.dialogTitle')}
              </DialogTitle>
              <p className='text-sm font-normal leading-[1.4] text-[#171a20]'>
                {t('accountDeletion.dialogBody')}
              </p>
            </div>

            <div className='flex flex-col gap-4'>
              <div className='flex flex-col gap-2'>
                <Label className='text-xs font-medium leading-4 text-[#171a20]'>
                  {t('accountDeletion.reasonLabel')}
                </Label>
                <Select
                  value={reason || NONE}
                  onValueChange={v =>
                    setReason(v === NONE ? '' : (v as AccountDeletionReason))
                  }
                >
                  <SelectTrigger className='h-10 rounded-xl px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20]'>
                    <SelectValue
                      placeholder={t('accountDeletion.reasonPlaceholder')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>
                      {t('accountDeletion.noReason')}
                    </SelectItem>
                    {REASONS.map(r => (
                      <SelectItem key={r} value={r}>
                        {t(REASON_LABEL_KEY[r])}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {reason === 'Other' && (
                <div className='flex flex-col gap-2'>
                  <Label
                    htmlFor='account-deletion-other-reason'
                    className='text-xs font-medium leading-4 text-[#171a20]'
                  >
                    {t('accountDeletion.otherReasonLabel')}
                  </Label>
                  <Input
                    id='account-deletion-other-reason'
                    value={otherReason}
                    maxLength={2000}
                    placeholder={t('accountDeletion.otherReasonPlaceholder')}
                    onChange={e => setOtherReason(e.target.value)}
                    className={FORM_FIELD_CLASS}
                  />
                </div>
              )}
              <div className='flex flex-col gap-2'>
                <Label
                  htmlFor='del-pw'
                  className='text-xs font-medium leading-4 text-[#171a20]'
                >
                  {t('accountDeletion.passwordLabel')}
                </Label>
                <Input
                  id='del-pw'
                  type='password'
                  value={password}
                  onChange={e => {
                    setPassword(e.target.value)
                    setError(null)
                  }}
                  className={cn(FORM_FIELD_CLASS, error && 'border-[#d63b33]')}
                />
                {error && (
                  <p className='text-xs font-normal leading-4 text-[#d63b33]'>
                    {error}
                  </p>
                )}
              </div>
            </div>

            <div className='flex items-center justify-end gap-4'>
              <Button
                variant='outline'
                className={confirmDeleteCancelBtnClass}
                onClick={() => {
                  setOpen(false)
                  reset()
                }}
              >
                {t('accountDeletion.cancel')}
              </Button>
              <Button
                className={confirmBrandBtnClass}
                onClick={confirm}
                disabled={request.isPending}
              >
                {t('accountDeletion.confirm')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </SettingsCard>
  )
}

export default AccountDeletionPanel
